// AuthContext.js 
// Provides autehntication state accross the application. It uses 'onAuthStateChanged' to listen from Firebase to track the  
// user's authentication status and provide context that can be consumed by components like 'Login.js' and 'Register.js'.
// AuthContext is created using 'createContext', which is later exported and used by useAuth for easier access within components.
// AuthProvider is a context provider component that wraps your application's components, making the auth state available to any component that calls useAuth.
// Inside AuthProvider, there is an observer on the Firebase Auth object with onAuthStateChanged. This observer is called whenever the user's sign-in state changes.
// When the authentication state changes (a user logs in or out), the observer updates currentUser with the new user object, or null if the user signs out.
// The loading state ensures that the application renders children only after the initial authentication status is retrieved, 
// preventing any flicker or unauthorized access to components that require an authenticated user.

import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../../FirebaseConfig';
import { onAuthStateChanged, signOut } from 'firebase/auth';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false); 
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        try {
          const tokenResult = await user.getIdTokenResult();
          setIsAdmin(tokenResult.claims.isAdmin === true);
        } catch (_) {
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

    // Logout function
    const logout = async () => {
      await signOut(auth);
    };
    
    const value = {
      currentUser,
      isAdmin, 
      loading,
      logout, 
    };
  
  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
